import i18next from "i18next";
import { initReactI18next } from "react-i18next";
// Mantine's date components render their popup through dayjs, which knows only
// English until a locale module is imported for its side effect — without this,
// App's DatesProvider locale silently resolves back to English. It lives here
// rather than in App.tsx because it is locale wiring like everything else in
// this module, and because main.tsx loads this module before rendering, so the
// registration still happens before the first calendar can open.
import "dayjs/locale/de";
import type { Locale } from "@iso27001/shared";
import { apiFetch } from "../api/client.js";
import de from "./de.json";
import en from "./en.json";
import { DEFAULT_LOCALE, resolveLocale } from "./resolve-locale.js";

const STORAGE_KEY = "isms-locale";

/**
 * localStorage wrapped to tolerate being unavailable or throwing. Some
 * locked-down enterprise browser configurations disable Web Storage
 * entirely, which makes even a bare property read throw — a language
 * preference is not worth taking down the whole app over, so every call
 * site goes through these instead of touching `localStorage` directly.
 */
function readStoredLocale(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Same rationale as readStoredLocale: losing the remembered choice for
    // this browser is fine, crashing the app over it is not.
  }
}

/**
 * Mirrors the active language onto `<html lang>`.
 *
 * index.html ships `lang="de"` as the pre-hydration default and nothing used
 * to update it, so an English user got an entirely English interface inside a
 * document still declaring itself German: screen readers pronounced English
 * words with German phonetics, browsers offered to translate a page already
 * in the reader's language, and `:lang()` and hyphenation matched the wrong
 * rules.
 *
 * Guarded on `document` because this module is imported by tests running
 * under plain Node with no DOM — the same reason initI18n is a function
 * rather than an import-time side effect (see below).
 */
function syncDocumentLang(lng: string): void {
  if (typeof document !== "undefined") document.documentElement.lang = lng;
}

/**
 * Initialises i18next. Exported and called once from main.tsx rather than
 * run as a top-level side effect of importing this module: AuthContext,
 * Header, LanguageSwitcher and PublicDocsLayout all import this module
 * (directly or transitively), and this repo's vitest suite runs under plain
 * Node with no jsdom/happy-dom configured — no `localStorage`, no
 * `navigator`. Doing browser I/O at import time meant merely importing any
 * of those four components crashed test collection before a single
 * assertion ran, for every future test that renders one of them, not just
 * this module's own.
 */
export function initI18n(): void {
  // Registered before init() on purpose: init() resolves the initial language
  // through changeLanguage, which emits this event, so one handler covers
  // both first paint and every later switch.
  i18next.on("languageChanged", syncDocumentLang);
  void i18next.use(initReactI18next).init({
    resources: { de: { translation: de }, en: { translation: en } },
    lng: resolveLocale({
      userLocale: null, // not known until the session loads; applyUserLocale fixes it up
      stored: readStoredLocale(),
      acceptLanguage: navigator.language ?? null,
    }),
    // German is the source language, so a missing English key renders German
    // rather than a raw key. An auditor shown "risiken.form.title" is worse
    // served than one shown German.
    fallbackLng: DEFAULT_LOCALE,
    interpolation: { escapeValue: false }, // React already escapes
  });
}

/**
 * Switch language now, and remember it. Rejects if the server persist fails
 * (an ApiError from apiFetch, or a network error) — callers must not
 * silently discard that with `void`: the local i18next language and
 * localStorage have already changed by the time the request could fail, so
 * swallowing the error would leave the UI showing a language the account
 * was never actually updated to remember, which resolveLocale would then
 * silently revert on the next session load elsewhere (userLocale outranks
 * stored).
 */
export async function changeLocale(locale: Locale, opts: { persistToServer: boolean }): Promise<void> {
  writeStoredLocale(locale);
  await i18next.changeLanguage(locale);
  if (opts.persistToServer) {
    await apiFetch("/api/auth/locale", { method: "PATCH", body: JSON.stringify({ locale }) });
  }
}

/** Apply a signed-in user's stored preference once the session is known. */
export async function applyUserLocale(userLocale: Locale | null): Promise<void> {
  const next = resolveLocale({
    userLocale,
    stored: readStoredLocale(),
    acceptLanguage: navigator.language ?? null,
  });
  if (i18next.language !== next) await i18next.changeLanguage(next);
}

export default i18next;
